import { useNotification } from '@/hooks';
import {
  useCreateRequestMutation,
  useUpdateRequestMutation,
  useAssignAssetToRequestMutation,
} from '../services';
import { useUpdateAssetMutation } from '@/pages/Inventory/pages/Assets/service';
import { RequestSchema } from '../models';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useUserInfo } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const useRequestActions = () => {
  const [createRequest, { isLoading: isCreatingRequest }] =
    useCreateRequestMutation();
  const [updateRequest, { isLoading: isUpdatingRequest }] =
    useUpdateRequestMutation();
  const [assignAssetToRequest] = useAssignAssetToRequestMutation();
  const [updateAsset] = useUpdateAssetMutation();

  const { showNotification } = useNotification();
  const userInfo = useUserInfo();
  const navigate = useNavigate();

  const onCreateRequest = async (data: RequestSchema, reset: () => void) => {
    try {
      const transformedData = {
        userId: data.userId,
        assetTypeId: data.type === 'OTHER' ? undefined : data.type,
        otherType: data.otherType,
        note: data.note,
        status: data.status,
        neededAt: data.neededAt
          ? dayjs(data.neededAt).format('YYYY-MM-DD')
          : null,
        email: userInfo?.email,
        fullName: userInfo?.firstName && userInfo?.lastName 
          ? `${userInfo.firstName} ${userInfo.lastName}`.trim()
          : undefined,
      };

      await createRequest(transformedData).unwrap();
      showNotification(
        'Petición creada correctamente',
        NotificationSeverity.success
      );
      reset();
      navigate('/inventory/requests');
    } catch (error: any) {
      showNotification(
        error?.data?.message ||
          error?.message ||
          'Ocurrió un error al crear la petición. Inténtalo nuevamente.',
        NotificationSeverity.error
      );
    }
  };

  const onUpdateRequest = async (
    requestId: string,
    body: any,
    onSuccess?: () => void
  ) => {
    try {
      const transformedBody = {
        ...body,
        neededAt: body.neededAt
          ? dayjs(body.neededAt).format('YYYY-MM-DD')
          : null,
        email: userInfo?.email,
        fullName: userInfo?.firstName && userInfo?.lastName 
          ? `${userInfo.firstName} ${userInfo.lastName}`.trim()
          : undefined,
      };

      await updateRequest({ requestId, body: transformedBody }).unwrap();
      showNotification(
        'Solicitud actualizada correctamente',
        NotificationSeverity.success
      );
      if (onSuccess) onSuccess();
      navigate('/inventory/requests');
    } catch (error: any) {
      showNotification(
        error?.data?.message ||
          error?.message ||
          'Error al actualizar la solicitud',
        NotificationSeverity.error
      );
    }
  };

  const onAssignAsset = async (requestId: string, body: any) => {
    try {
      await assignAssetToRequest({
        requestId,
        assetId: body.assignedAssetId,
      }).unwrap();

      if (body.assignedAssetId && body.userId) {
        await updateAsset({
          assetId: body.assignedAssetId,
          body: {
            userId: body.userId,
          },
        }).unwrap();
      }

      showNotification(
        'Equipo asignado correctamente',
        NotificationSeverity.success
      );
    } catch (error: any) {
      showNotification(
        error?.data?.message || error?.message || 'Error al asignar el equipo',
        NotificationSeverity.error
      );
    }
  };

  const showAssetSelectionWarning = () => {
    showNotification(
      'Debes seleccionar un equipo',
      NotificationSeverity.warning
    );
  };

  const onDeleteRequest = async (requestId: string, onSuccess?: () => void) => {
    try {
      await updateRequest({
        requestId,
        body: { status: 'disabled' } as any,
      }).unwrap();
      showNotification(
        'Solicitud eliminada correctamente',
        NotificationSeverity.success
      );
      if (onSuccess) onSuccess();
    } catch (error: any) {
      showNotification(
        'Ocurrió un error al eliminar la solicitud. Inténtalo nuevamente.',
        NotificationSeverity.error
      );
    }
  };

  return {
    onCreateRequest,
    onUpdateRequest,
    onAssignAsset,
    showAssetSelectionWarning,
    isCreatingRequest,
    isUpdatingRequest,
    onDeleteRequest,
  };
};

export default useRequestActions;
